/**
 * @file TaskRouteGuard.jsx
 * @description Route guard component to protect task-related routes based on user session and taskRole.
 */

import React from "react";
import { Route, Redirect } from "react-router-dom";
import { useSession } from "../firebase/UserProvider";
import { useTaskRole } from "../hooks/useTaskRole";

/**
 * Route guard for task management modules.
 * @param {Object} props
 * @param {React.Component} props.component
 * @param {'taskUser' | 'taskAdmin'} props.requiredRole
 * @returns {React.ReactElement}
 */
export default function TaskRouteGuard({ component: Component, requiredRole, ...rest }) {
  const { user, loading: sessionLoading } = useSession();
  const { isTaskUser, isTaskAdmin, loading: roleLoading } = useTaskRole();

  return (
    <Route
      {...rest}
      render={(props) => {
        // While checking auth session or user privileges from Firestore, do not redirect
        if (sessionLoading || roleLoading) {
          return null;
        }

        // taskAdmin routes: only TaskAdmin; taskUser routes: both TaskUser and TaskAdmin
        const isAuthorized =
          requiredRole === "taskAdmin" ? isTaskAdmin : isTaskUser;

        if (!!user && isAuthorized) {
          return <Component {...props} />;
        } else {
          if (!user) {
            sessionStorage.setItem("redirectPath", props.location.pathname + props.location.search);
          }
          return <Redirect to="/login" />;
        }
      }}
    />
  );
}
